/** @format */

import {useRef, useState} from 'react';

import {useCookies} from 'react-cookie';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {SignUp as SignUpScreen} from 'ui';
import {useAuthContext} from 'contexts/AuthContext';
import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';
import {secondLevelDomain} from 'helpers/routerHelpers';

import {useSignUpMutation} from './graphql/__generated__';

const SignUp = () => {
  const navigate = useNavigate();
  const [cookies] = useCookies();
  const authContext = useAuthContext();
  const {switchCompany} = useCompanyContext();
  const {mixpanel} = useMixpanelContext();
  const {t, i18n} = useTranslation();

  const [serverError, setServerError] = useState<string>();
  const [signUp, {loading}] = useSignUpMutation();
  // signUpWithCompany is not idempotent (creates a user + company), so a
  // fast double-click must not fire it twice. The button's disabled state is a
  // render behind the mutate() call; a synchronous ref closes that gap.
  const submittingRef = useRef(false);

  const handleSubmit = async ({
    phone,
    password,
    email,
    promocode,
  }: {
    phone: string;
    password: string;
    email: string;
    promocode: string;
  }) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    mixpanel.track('click_sign_up_button');
    setServerError(undefined);
    try {
      const {data} = await signUp({
        variables: {
          phone: phone.trim(),
          password,
          email: email.trim() || null,
          referrer: cookies.partner || null,
          lang: i18n.language,
          // Optional field: send null (omit) rather than '' when untouched, so
          // the backend doesn't attempt a promo lookup on an empty string.
          promoCode: promocode.trim() || null,
        },
      });

      if (!data) return;

      await authContext.signUp(data.signUpWithCompany);
      const company = data.signUpWithCompany.user.firstCompany;
      switchCompany(company);

      const companyId = company?.id;

      if (!companyId) {
        // Signup succeeded but the backend returned no company (should not
        // happen for signUpWithCompany). The session is already live — send the
        // user to the app default instead of stranding them on the form.
        navigate('/grouping', {replace: true});
        return;
      }

      navigate(
        {
          pathname: '/home/project/wizard/created',
          search: `?companyId=${companyId}&video=1`,
        },
        {replace: true},
      );
    } catch (e) {
      if (!(e instanceof Error)) return;
      // The backend returns opaque codes; only invalid_promo_code is known.
      // Everything else is shown verbatim in the general server-error slot —
      // we don't guess which field a code belongs to.
      setServerError(
        e.message === 'invalid_promo_code'
          ? t(
              'Вы ввели неверный промокод, попробуйте еще раз',
              'Вы ввели неверный промокод, попробуйте еще раз',
            )
          : e.message,
      );
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <SignUpScreen
      submitState={loading ? 'submitting' : 'idle'}
      serverError={serverError}
      onSubmit={handleSubmit}
      onSignInClick={() => navigate('/auth/sign-in')}
      onLogoClick={() => window.location.assign(`https://${secondLevelDomain()}/`)}
      adsensorDomain={secondLevelDomain()}
    />
  );
};

export default SignUp;
